import { useEffect,useState } from 'react';

import { useAppDispatch } from 'app/hooks';
import { fetchAclEntries } from 'common/services/api/Acls/AclEntries.thunk';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { fetchLicense } from 'common/services/api/Licenses/Licenses.thunk';
import { Acl, AppSuiteTenant, License,UserGroup } from 'common/types/Entities';
import { handleListFetchError } from 'common/utils/errorHandlers';
import { useSnackbar } from 'notistack';

import { _formatProductEntitlementsWithLicenseInfo } from '../helpers';
import { UserGroupReportProductEntitlements } from '../typeDefs';


export const useFetchProductEntitlements = (userGroups: UserGroup[], currentTenant: AppSuiteTenant): {
    processing: boolean,
    productEntitlements: UserGroupReportProductEntitlements[]
} => {
    const [productEntitlements, setProductEntitlements] = useState<UserGroupReportProductEntitlements[]>([]);
    const [processing, setProcessing] = useState<boolean>(false);

    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const { id: tenantId, isLicenseRequired } = currentTenant;

    const _handleError = (error: unknown) => {
        const err = error as Error;
        const statusCode = extractStatusCode(err.message);
        handleListFetchError(statusCode, 'Product Entitlements', enqueueSnackbar);
        setProcessing(false);
    };

    const _getFetchAclEntriesPromises = () => {
        const fetchAclEntriesPromises = [];

        for (const userGroup of userGroups) {
            fetchAclEntriesPromises.push(
                dispatch(
                    fetchAclEntries({
                        tenantId: tenantId,
                        usergroupId: userGroup.id
                    })
                )
                    .unwrap()
                    .catch((error) => { _handleError(error); })
            );
        }

        return fetchAclEntriesPromises;
    };

    const _getFetchLicenseEntriesPromises = (licenseIds: string[]) => {
        const fetchLicensesPromises = [];

        for (const licenseId of licenseIds) {
            fetchLicensesPromises.push(
                dispatch(
                    fetchLicense({
                        tenantId: tenantId,
                        id: licenseId ?? ''
                    })
                )
                    .unwrap()
                    .catch((error) => { _handleError(error); })
            );
        }

        return fetchLicensesPromises;
    };

    const _extractUniqueLicenseIds = (aclEntries: Acl[]) => {
        return aclEntries
            .map((aclEntry) => aclEntry.licenseId)
            .filter((aclEntryLicenseId, index, array) => array.indexOf(aclEntryLicenseId) === index) as string[];
    };

    const _getLicenseEntries = (licenses: License[]) => {
        return licenses.filter(item => !!item);
    };

    useEffect(() => {
        setProcessing(true);

        const fetchAclEntriesPromises = _getFetchAclEntriesPromises();
        Promise
            .all(fetchAclEntriesPromises)
            .then((aclEntriesByUserGroupId) => {
                const aclEntries = aclEntriesByUserGroupId
                    .filter(item => !!item)
                    .filter(item => item?.length)
                    .flat() as Acl[];

                const uniqueLicensesIds = _extractUniqueLicenseIds(aclEntries);

                const fetchLicensesPromises = _getFetchLicenseEntriesPromises(uniqueLicensesIds);

                Promise
                    .all(fetchLicensesPromises)
                    .then((licenses) => {
                        const licenseEntries = _getLicenseEntries(licenses as License[]);
                        const formattedProductEntitlements = _formatProductEntitlementsWithLicenseInfo({
                            acls: aclEntries,
                            licenses: licenseEntries,
                            isLicenseRequired
                        });

                        setProductEntitlements(formattedProductEntitlements);
                        setProcessing(false);
                    }).catch((error) => { _handleError(error); });
            }).catch((error) => { _handleError(error); });
    }, []);

    return {
        processing,
        productEntitlements
    };
};
